// UPost.com - Dashboard
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Logo } from '../components/Logo';
import { DOCUMENT_TYPES } from '../config';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Zap, Sparkles, Shield, BookOpen, Film, Loader2 } from 'lucide-react';
import { CreditPurchaseModal } from '../components/CreditPurchaseModal';
import { CraftCreditPurchaseModal } from '../components/CraftCreditPurchaseModal';
import { TodaysInspiration } from '../components/TodaysInspiration';

export function Dashboard() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiUsage, setAiUsage] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newDocTitle, setNewDocTitle] = useState('');
  const [newDocType, setNewDocType] = useState('novel');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showCollabTip, setShowCollabTip] = useState(true);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [creditBalance, setCreditBalance] = useState(0);
  const [craftCredits, setCraftCredits] = useState(0);
  
  // Suggestion box state
  const [showSuggestionBox, setShowSuggestionBox] = useState(false);
  const [suggestionText, setSuggestionText] = useState('');
  const [suggestionCategory, setSuggestionCategory] = useState('feature');
  const [submittingSuggestion, setSubmittingSuggestion] = useState(false);
  
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
    // Refresh user data to ensure is_super_admin flag is loaded
    if (refreshUser) {
      refreshUser();
    }
    
    // Poll for unread comments every 30 seconds
    loadUnreadCounts();
    const interval = setInterval(loadUnreadCounts, 30000);
    
    // Handle payment success from Stripe redirect
    handlePaymentReturn();
    
    return () => clearInterval(interval);
  }, []);
  
  const handlePaymentReturn = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const paymentStatus = urlParams.get('payment');
    
    if (sessionId && paymentStatus === 'success') {
      // Poll payment status up to 5 times
      for (let i = 0; i < 5; i++) {
        try {
          const response = await api.request(`/payments/credits/status/${sessionId}`);
          
          if (response.payment_status === 'paid') {
            // Success! Show alert and refresh data
            alert(`🎉 Payment successful! ${response.credits_added || 'Credits'} added to your account!`);
            loadData(); // Refresh credit balance
            // Clean up URL
            window.history.replaceState({}, '', '/dashboard');
            break;
          } else if (response.status === 'expired') {
            alert('Payment session expired. Please try again.');
            window.history.replaceState({}, '', '/dashboard');
            break;
          }
        } catch (error) {
          console.error('Payment status check failed:', error);
        }
        
        // Wait 2 seconds before next poll
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    } else if (paymentStatus === 'cancelled') {
      // User cancelled payment
      window.history.replaceState({}, '', '/dashboard');
    }
  };

  async function loadData() {
    try {
      const [docs, usage, credits, craftCreds] = await Promise.all([
        api.getDocuments(),
        api.getAIUsage(),
        api.request('/payments/credits/balance'),
        api.request('/credits/balance')
      ]);
      setDocuments(docs);
      setAiUsage(usage);
      setCreditBalance(credits.power_credits || 0);
      setCraftCredits(craftCreds.credits || 0);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  }
  
  const loadUnreadCounts = async () => {
    try {
      const counts = await api.request('/comments/unread-counts');
      setUnreadCounts(counts);
    } catch (err) {
      console.error('Failed to load unread counts:', err);
    }
  };

  async function handleCreateDocument() {
    if (!newDocTitle.trim()) {
      setError('Please enter a title');
      return;
    }

    setCreating(true);
    setError('');

    try {
      const doc = await api.createDocument(newDocTitle, newDocType);
      setShowCreateDialog(false);
      navigate(`/editor/${doc.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  }

  async function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    const validTypes = ['.txt', '.docx', '.doc'];
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    if (!validTypes.includes(fileExt)) {
      setError('Please upload a .txt or .docx file');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.request('/documents/upload', {
        method: 'POST',
        body: formData,
        // Don't set Content-Type header, let browser set it with boundary
        headers: {}
      });

      // Redirect to the newly created document
      navigate(`/editor/${response.document_id}`);
    } catch (err) {
      setError(err.message || 'Failed to upload file');
    } finally {
      setUploading(false);
      // Reset file input
      e.target.value = '';
    }
  }

  async function handleDeleteDocument(id) {
    if (!window.confirm('Archive this document?')) return;
    
    try {
      await api.deleteDocument(id);
      setDocuments(documents.filter(d => d.id !== id));
    } catch (err) {
      alert('Failed to delete document');
    }
  }

  function formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  const usagePercent = aiUsage ? (aiUsage.ai_words_used / aiUsage.ai_words_limit * 100) : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <Link to="/">
              <Logo variant="minimal" size="md" />
            </Link>
            <div className="flex items-center gap-4">
              {user?.is_super_admin && (
                <Link to="/admin">
                  <Button variant="outline" className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                    Admin Dashboard
                  </Button>
                </Link>
              )}
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowSuggestionBox(true)}
                className="flex items-center gap-2 border-blue-300 hover:bg-blue-50"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  <line x1="9" y1="9" x2="15" y2="9"/>
                  <line x1="9" y1="13" x2="15" y2="13"/>
                </svg>
                💡 Suggestion Box
              </Button>
              <Link to="/settings">
                <Button variant="outline" size="sm">Settings</Button>
              </Link>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    {user?.full_name || user?.email}
                  </div>
                  <div className="text-xs text-gray-500">
                    {user?.email}
                  </div>
                </div>
                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-semibold">
                  {user?.subscription_tier?.toUpperCase()}
                </span>
              </div>
              <Button variant="ghost" onClick={logout}>Log Out</Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Today's Inspiration Card */}
        <div className="mb-8">
          <TodaysInspiration />
        </div>

        {documents.length > 0 && (
          <>
            {documents.filter(d => d.is_collaborated).length > 0 && (
              <div className="mb-8">
                <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-t-lg p-4 text-white">
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    👥 Shared With You - Collaboration Invitations
                  </h2>
                  <p className="text-sm opacity-90 mt-1">
                    You've been invited to collaborate on {documents.filter(d => d.is_collaborated).length === 1 ? 'this document' : 'these documents'}. Click to open and start commenting!
                  </p>
                </div>

                {/* Collaboration Instructions */}
                {showCollabTip && (
                  <Alert className="rounded-t-none bg-blue-50 border-blue-200 border-t-0">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">📝</div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-blue-900 mb-2">How to Leave Comments & Feedback</h4>
                          <div className="text-sm text-blue-800 space-y-2">
                            <div className="bg-white rounded-lg p-3 border border-blue-200">
                              <ol className="list-decimal list-inside space-y-1.5 text-blue-800">
                                <li><strong>Click the document below</strong> to open it</li>
                                <li>Look at the <strong>line numbers</strong> on the left side</li>
                                <li>Click the <strong>"Comments"</strong> button in the toolbar</li>
                                <li>Enter the <strong>line number</strong> you're commenting on</li>
                                <li>Type your feedback and click send</li>
                              </ol>
                            </div>
                            <p className="text-xs">
                              💡 The owner will see your comments <strong>instantly</strong> or get an email if offline!
                            </p>
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowCollabTip(false)}
                        className="text-blue-600 flex-shrink-0"
                      >
                        Got it
                      </Button>
                    </div>
                  </Alert>
                )}

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 bg-purple-50 border-2 border-purple-200 rounded-b-lg">
                  {documents.filter(d => d.is_collaborated).map((doc) => (
                    <Card 
                      key={doc.id} 
                      className="border-2 border-purple-400 bg-white hover:shadow-xl transition-all cursor-pointer hover:scale-105"
                      onClick={() => navigate(`/editor/${doc.id}`)}
                    >
                      <CardHeader>
                        <div className="flex items-center justify-between mb-2">
                          <span className="px-3 py-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-xs rounded-full font-bold animate-pulse">
                            👥 SHARED WITH YOU
                          </span>
                          {unreadCounts[doc.id] > 0 && (
                            <span className="px-2 py-1 bg-red-500 text-white text-xs rounded-full font-bold">
                              {unreadCounts[doc.id]} new
                            </span>
                          )}
                        </div>
                        <CardTitle className="flex items-center gap-2">
                          <span className="text-2xl">
                            {DOCUMENT_TYPES.find(t => t.value === doc.doc_type)?.icon || '📄'}
                          </span>
                          <span className="truncate flex-1">{doc.title}</span>
                        </CardTitle>
                        <CardDescription>
                          <div className="space-y-1">
                            <div>{doc.doc_type.charAt(0).toUpperCase() + doc.doc_type.slice(1)}</div>
                            <div className="text-purple-600 font-semibold">
                              Click to view & comment →
                            </div>
                            <div className="text-xs text-gray-600 mt-2">
                              {doc.word_count.toLocaleString()} words • Last saved: {formatDate(doc.last_saved)}
                            </div>
                          </div>
                        </CardDescription>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* MY DOCUMENTS SECTION */}
            {documents.filter(d => !d.is_collaborated).length > 0 && (
              <div className="mb-6">
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  📚 My Documents
                </h2>
              </div>
            )}

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {documents.filter(doc => !doc.is_collaborated).map((doc) => (
                <Card 
                  key={doc.id} 
                  className="hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/editor/${doc.id}`)}
                >
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <span className="text-2xl">
                        {DOCUMENT_TYPES.find(t => t.value === doc.doc_type)?.icon || '📄'}
                      </span>
                      <span className="truncate flex-1">{doc.title}</span>
                      {unreadCounts[doc.id] > 0 && (
                        <span className="px-2 py-1 bg-red-500 text-white text-xs rounded-full font-bold whitespace-nowrap animate-pulse">
                          {unreadCounts[doc.id]} new
                        </span>
                      )}
                    </CardTitle>
                    <CardDescription>
                      {doc.doc_type.charAt(0).toUpperCase() + doc.doc_type.slice(1)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm text-gray-600">
                      <div>{doc.word_count.toLocaleString()} words</div>
                      <div>{doc.ai_word_count.toLocaleString()} AI-generated</div>
                      <div>Last saved: {formatDate(doc.last_saved)}</div>
                    </div>
                    {(
                      <div className="flex flex-col gap-2 mt-4">
                        {doc.doc_type === 'novel' && (
                          <Button
                            size="sm"
                            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/book-cover/${doc.id}`);
                            }}
                          >
                            🎨 Design Cover for Kindle
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteDocument(doc.id);
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* UPLOAD / CREATE NEW DOCUMENT - AFTER DOCUMENTS */}
            <div className="mb-8 p-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border-2 border-dashed border-purple-300">
              <h3 className="text-lg font-bold mb-4 text-gray-900">📝 Create or Upload Document</h3>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => document.getElementById('file-upload').click()}
                  className="flex-1"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17 8 12 3 7 8"></polyline>
                    <line x1="12" y1="3" x2="12" y2="15"></line>
                  </svg>
                  Upload File (.txt, .docx)
                </Button>
                <Button onClick={() => setShowCreateDialog(true)} className="flex-1">
                  + Create New Document
                </Button>
              </div>
            </div>
          </>
        )}

        {/* ========================================= */}
        {/* INFO CARDS SECTION */}
        {/* ========================================= */}

        {/* Craft Edit Card - Full Width */}
        <Card className="mb-6 bg-gradient-to-br from-purple-600 via-purple-500 to-blue-600 text-white border-0 shadow-xl">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-3xl">✨</span>
                  <h3 className="text-2xl font-bold">NEW: AI Craft Editor</h3>
                  <span className="px-3 py-1 bg-yellow-400 text-yellow-900 rounded-full text-xs font-bold animate-pulse">
                    PREMIUM
                  </span>
                </div>
                
                <p className="text-purple-100 mb-4 text-sm md:text-base">
                  Transform your writing from <strong>good to exceptional</strong>. Our AI Craft Editor focuses on HOW you write, not what you write.
                </p>

                <div className="grid sm:grid-cols-2 gap-3 mb-4">
                  <div className="bg-white/10 backdrop-blur rounded-lg p-3">
                    <div className="font-semibold mb-1">📖 Elevate Your Prose</div>
                    <div className="text-xs text-purple-100">Add vivid imagery, sensory details & depth</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-lg p-3">
                    <div className="font-semibold mb-1">🎵 Master Rhythm</div>
                    <div className="text-xs text-purple-100">Perfect sentence flow & pacing</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-lg p-3">
                    <div className="font-semibold mb-1">💬 Polish Dialogue</div>
                    <div className="text-xs text-purple-100">Natural, authentic conversations</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-lg p-3">
                    <div className="font-semibold mb-1">🎨 Show, Don't Tell</div>
                    <div className="text-xs text-purple-100">Transform telling into showing</div>
                  </div>
                </div>

                <div className="bg-white/20 backdrop-blur rounded-lg p-4 mb-4">
                  <div className="font-semibold mb-2 text-sm">💡 Example Transformation:</div>
                  <div className="space-y-2 text-xs">
                    <div className="bg-red-900/30 rounded p-2">
                      <div className="font-semibold text-red-200 mb-1">Before (Basic):</div>
                      <div className="italic text-white/90">"He was angry. He left the room."</div>
                    </div>
                    <div className="bg-green-900/30 rounded p-2">
                      <div className="font-semibold text-green-200 mb-1">After (Literary):</div>
                      <div className="italic text-white/90">"His jaw clenched as heat flooded his chest. Without a word, he turned and slammed the door behind him."</div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <div className="flex-1">
                    {user?.is_super_admin ? (
                      <div className="bg-white/20 backdrop-blur rounded-lg p-3">
                        <div className="text-sm font-semibold">Admin Access:</div>
                        <div className="text-3xl font-bold">∞ Unlimited</div>
                        <div className="text-xs text-purple-100">Full craft editing access - no limits!</div>
                      </div>
                    ) : craftCredits > 0 ? (
                      <div className="bg-white/20 backdrop-blur rounded-lg p-3">
                        <div className="text-sm font-semibold">Your Credit Balance:</div>
                        <div className="text-3xl font-bold">{craftCredits}</div>
                        <div className="text-xs text-purple-100">Perfect for ~{Math.floor(craftCredits / 15)} medium edits</div>
                      </div>
                    ) : (
                      <div className="text-sm">
                        <div className="font-semibold mb-1">💳 Pay-as-you-go pricing</div>
                        <div className="text-xs text-purple-100">5-30 credits per edit • Packages from $9.99</div>
                      </div>
                    )}
                  </div>
                  <Button 
                    onClick={() => {
                      if ((user?.is_super_admin || craftCredits > 0) && documents.length > 0) {
                        navigate(`/editor/${documents[0].id}`);
                      } else if (user?.is_super_admin) {
                        alert('Create a document first to use the Craft Editor! 📝');
                      } else {
                        setShowCreditModal(true);
                      }
                    }}
                    className="bg-white text-purple-600 hover:bg-purple-50 font-bold shadow-lg"
                  >
                    {user?.is_super_admin ? '🎨 Open Craft Editor' : craftCredits > 0 ? '🎨 Use Craft Editor' : '✨ Get Started - $9.99'}
                  </Button>
                </div>
              </div>
              
              <div className="hidden lg:block">
                <div className="text-8xl opacity-90">🎭</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Screenplay Conversion Card - Full Width */}
        <Card className="mb-6 bg-gradient-to-br from-blue-600 to-indigo-600 text-white border-0 shadow-xl">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-3xl">🎬</span>
                  <h3 className="text-2xl font-bold">Screenplay Conversion</h3>
                </div>
                
                <p className="text-blue-100 mb-3 text-sm md:text-base">
                  Transform your novel into a professionally formatted screenplay in <strong>30 minutes</strong>.
                </p>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <div className="text-sm">
                    <div className="font-semibold mb-1">Industry-standard formatting</div>
                    <div className="text-xs text-blue-100">Scene headings • Action lines • Dialogue</div>
                  </div>
                  <Link to="/screenplay-conversion">
                    <Button className="bg-white text-blue-600 hover:bg-blue-50 font-bold shadow-lg">
                      Convert to Screenplay →
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Power Credits Card - Full Width */}
        <Card className="mb-6 border-2 border-yellow-200 bg-gradient-to-br from-yellow-50 to-orange-50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="w-6 h-6 text-yellow-600" />
                <CardTitle>Power Credits</CardTitle>
              </div>
              <Button 
                size="sm" 
                onClick={() => setShowCreditModal(true)}
                className="bg-yellow-600 hover:bg-yellow-700"
              >
                💳 Buy Credits
              </Button>
            </div>
            <CardDescription className="text-gray-700">
              <strong>Unlock premium AI features on-demand</strong>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row items-start gap-4">
              {/* Credit Balance */}
              <div className="flex-1">
                <div className="text-4xl font-bold text-yellow-700 mb-2">
                  {creditBalance} Credits
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  {creditBalance === 0 ? (
                    "Purchase power credits to unlock premium features!"
                  ) : (
                    `You have ${creditBalance} credit${creditBalance !== 1 ? 's' : ''} available`
                  )}
                </p>
              </div>
              
              {/* What Power Credits Do */}
              <div className="flex-1 bg-white/70 rounded-lg p-4 border border-yellow-200">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-yellow-600" />
                  What Can You Do With Power Credits?
                </h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-600 font-bold">•</span>
                    <span><strong>Multi-Revision AI:</strong> Generate 3-5 different versions of your text at once (1 credit per use)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-600 font-bold">•</span>
                    <span><strong>Bypass Limits:</strong> Keep writing even after hitting your monthly word limit</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-600 font-bold">•</span>
                    <span><strong>Premium Features:</strong> Access advanced AI capabilities without upgrading your plan</span>
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Two-Column Grid: Multi-Revision AI + AI Usage */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Multi-Revision AI Card */}
          <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-purple-600">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                  <path d="M2 17l10 5 10-5"></path>
                  <path d="M2 12l10 5 10-5"></path>
                </svg>
                Multi-Revision AI
                {(user?.subscription_tier === 'pro' || user?.subscription_tier === 'premium' || user?.is_super_admin) && (
                  <span className="px-2 py-0.5 bg-purple-600 text-white rounded-full text-xs font-bold ml-auto">
                    UNLOCKED
                  </span>
                )}
              </CardTitle>
              <CardDescription>Generate multiple AI versions at once - compare & choose the best</CardDescription>
            </CardHeader>
            <CardContent>
              {/* What You Get */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-3 mb-3 border border-purple-200">
                <div className="font-semibold text-purple-900 mb-2 text-sm">
                  {user?.is_super_admin ? '👑 Admin Access' : user?.subscription_tier === 'premium' ? '⭐ Premium Plan' : user?.subscription_tier === 'pro' ? '💎 Pro Plan' : '🆓 Free Plan'}
                </div>
                <div className="text-2xl font-bold text-purple-600 mb-1">
                  {user?.is_super_admin || user?.subscription_tier === 'premium' ? '5' : user?.subscription_tier === 'pro' ? '3' : '1'} AI Version{(user?.is_super_admin || user?.subscription_tier === 'premium' || user?.subscription_tier === 'pro') ? 's' : ''}
                </div>
                <p className="text-xs text-gray-600">
                  {user?.is_super_admin || user?.subscription_tier === 'premium' || user?.subscription_tier === 'pro' 
                    ? 'Generate multiple versions per AI request in the editor' 
                    : 'Get only 1 version per AI request (upgrade for 3-5 versions)'}
                </p>
              </div>

              {/* How It Works */}
              <div className="bg-white rounded-lg p-3 mb-3 border border-gray-200">
                <div className="text-xs font-semibold text-gray-700 mb-2">💡 How it works:</div>
                <div className="text-xs text-gray-600 space-y-1">
                  <div>1️⃣ Open any document in the <strong>Editor</strong></div>
                  <div>2️⃣ Use AI writing features (Generate, Expand, etc.)</div>
                  <div>3️⃣ Get {user?.is_super_admin || user?.subscription_tier === 'premium' ? '5' : user?.subscription_tier === 'pro' ? '3' : '1'} version{(user?.is_super_admin || user?.subscription_tier === 'premium' || user?.subscription_tier === 'pro') ? 's' : ''} to compare side-by-side</div>
                  <div>4️⃣ Pick your favorite & insert into document</div>
                </div>
              </div>

              {/* Free User: Upgrade CTA */}
              {user?.subscription_tier === 'free' && (
                <>
                  <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-3 mb-3 border-2 border-yellow-300">
                    <div className="text-sm font-semibold text-gray-900 mb-2">🚀 Unlock Multi-Revision AI</div>
                    <ul className="text-xs text-gray-700 space-y-1 mb-3">
                      <li>✓ <strong>Pro Plan:</strong> 3 AI versions ($15/mo)</li>
                      <li>✓ <strong>Premium Plan:</strong> 5 AI versions ($25/mo)</li>
                      <li>✓ Compare styles & pick the best instantly</li>
                    </ul>
                  </div>
                  <Link to="/pricing">
                    <Button size="sm" className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700">
                      🚀 See Pricing Plans
                    </Button>
                  </Link>
                </>
              )}

              {/* Pro User: Upsell to Premium */}
              {user?.subscription_tier === 'pro' && (
                <div className="space-y-2">
                  {documents.length > 0 ? (
                    <Button
                      onClick={() => navigate(`/editor/${documents[0].id}`)}
                      size="sm"
                      className="w-full bg-purple-600 hover:bg-purple-700"
                    >
                      ✍️ Open Editor & Use Multi-Revision
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setShowCreateDialog(true)}
                      size="sm"
                      className="w-full bg-purple-600 hover:bg-purple-700"
                    >
                      📝 Create Document First
                    </Button>
                  )}
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-2 border border-purple-200">
                    <div className="text-xs text-gray-700 mb-1">
                      <strong>Want 5 versions?</strong> Upgrade to Premium for $10/mo more
                    </div>
                    <Link to="/pricing">
                      <Button variant="outline" size="sm" className="w-full border-purple-300 text-purple-700 text-xs">
                        Upgrade to Premium
                      </Button>
                    </Link>
                  </div>
                </div>
              )}

              {/* Premium/Admin User: Just Action Button */}
              {(user?.subscription_tier === 'premium' || user?.is_super_admin) && (
                <>
                  {documents.length > 0 ? (
                    <Button
                      onClick={() => navigate(`/editor/${documents[0].id}`)}
                      size="sm"
                      className="w-full bg-purple-600 hover:bg-purple-700"
                    >
                      ✍️ Open Editor & Use Multi-Revision
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setShowCreateDialog(true)}
                      size="sm"
                      className="w-full bg-purple-600 hover:bg-purple-700"
                    >
                      📝 Create Document to Get Started
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* AI Usage Stats Card */}
          {aiUsage && (
            <Card>
              <CardHeader>
                <CardTitle>AI Usage This Month</CardTitle>
                <CardDescription>
                  {aiUsage.ai_words_used.toLocaleString()} / {aiUsage.ai_words_limit === 999999999 ? 'Unlimited' : aiUsage.ai_words_limit.toLocaleString()} words
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                  <div
                    className="bg-purple-600 h-2 rounded-full transition-all"
                    style={{ width: `${Math.min(usagePercent, 100)}%` }}
                  />
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>{aiUsage.ai_words_remaining.toLocaleString()} words remaining</span>
                  {user?.subscription_tier === 'free' && (
                    <Link to="/upgrade" className="text-purple-600 hover:underline font-semibold">
                      Upgrade for More
                    </Link>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        {/* Resource Alerts */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <Alert className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-300">
            <Film className="w-5 h-5 text-purple-600" />
            <AlertDescription>
              <div className="mb-2">
                <strong className="text-purple-900">Bring Your Story to the Big Screen:</strong>
              </div>
              <div className="text-sm text-gray-700 mb-2">
                Learn how to sell your screenplay to Netflix, Hollywood, and streamers.
              </div>
              <Link to="/screenplay-guide">
                <Button size="sm" className="w-full bg-purple-600 hover:bg-purple-700">
                  Learn How →
                </Button>
              </Link>
            </AlertDescription>
          </Alert>

          <Alert className="bg-gradient-to-r from-orange-50 to-green-50 border-2 border-orange-300">
            <BookOpen className="w-5 h-5 text-orange-600" />
            <AlertDescription>
              <div className="mb-2">
                <strong className="text-orange-900">Publish on Amazon:</strong>
              </div>
              <div className="text-sm text-gray-700 mb-2">
                Step-by-step guide to get your book selling on Amazon KDP.
              </div>
              <Link to="/amazon-publishing">
                <Button size="sm" className="w-full bg-orange-600 hover:bg-orange-700">
                  Get Started →
                </Button>
              </Link>
            </AlertDescription>
          </Alert>

          <Alert className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-300">
            <Shield className="w-5 h-5 text-red-600" />
            <AlertDescription>
              <div className="mb-2">
                <strong className="text-red-900">Protect Your Work:</strong>
              </div>
              <div className="text-sm text-gray-700 mb-2">
                Register with copyright.gov ($45) + WGA ($20) to prevent theft.
              </div>
              <Link to="/copyright-protection">
                <Button size="sm" className="w-full bg-red-600 hover:bg-red-700">
                  Learn How →
                </Button>
              </Link>
            </AlertDescription>
          </Alert>
        </div>

        {/* Referral Program - EXPANDED */}
        <Card className="mb-8 bg-gradient-to-br from-purple-100 via-blue-50 to-purple-50 border-2 border-purple-300">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-3xl">💰</span>
                  <h3 className="text-2xl font-bold text-purple-900">
                    Affiliate Program - Earn BIG!
                  </h3>
                </div>
                
                <p className="text-gray-800 mb-4 font-semibold">
                  Turn your network into income! Earn commissions on EVERYTHING your referrals purchase.
                </p>

                {/* Commission Breakdown */}
                <div className="grid sm:grid-cols-2 gap-3 mb-4">
                  <div className="bg-white rounded-lg p-3 border border-purple-200">
                    <div className="font-bold text-purple-900 mb-1">💎 Pro Subscriptions</div>
                    <div className="text-2xl font-bold text-purple-600">25%</div>
                    <div className="text-xs text-gray-600">Lifetime recurring commission</div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-3 border border-blue-200">
                    <div className="font-bold text-blue-900 mb-1">✨ Craft Credits</div>
                    <div className="text-2xl font-bold text-blue-600">20%</div>
                    <div className="text-xs text-gray-600">Every credit pack purchase</div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-3 border border-green-200">
                    <div className="font-bold text-green-900 mb-1">📄 Services</div>
                    <div className="text-2xl font-bold text-green-600">20%</div>
                    <div className="text-xs text-gray-600">Copyright, Publishing, etc.</div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-3 border border-yellow-200">
                    <div className="font-bold text-yellow-900 mb-1">🎬 Conversions</div>
                    <div className="text-2xl font-bold text-yellow-600">20%</div>
                    <div className="text-xs text-gray-600">Screenplay & TV conversions</div>
                  </div>
                </div>

                {/* Bonus & Special Features */}
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-3 mb-4 border border-yellow-300">
                  <div className="font-semibold text-yellow-900 mb-2">🎁 BONUS REWARDS:</div>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>✓ <strong>5,000 free AI words</strong> per free signup (instant)</li>
                    <li>✓ <strong>Collaborators = Auto-Affiliates!</strong> Anyone you invite becomes YOUR referral</li>
                    <li>✓ <strong>Lifetime commissions</strong> - earn forever on subscriptions</li>
                    <li>✓ <strong>Free Pro subscription</strong> after hitting referral milestones</li>
                  </ul>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to="/referrals" className="flex-1">
                    <Button className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold">
                      🚀 View Affiliate Dashboard
                    </Button>
                  </Link>
                  <Button 
                    variant="outline" 
                    className="flex-1 border-2 border-purple-300 hover:bg-purple-50"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}?ref=${user?.referral_code || user?.id}`);
                      alert('Referral link copied! Share it to start earning! 💰');
                    }}
                  >
                    📋 Copy Referral Link
                  </Button>
                </div>
              </div>
              
              <div className="hidden lg:block ml-6">
                <div className="text-7xl">🎁</div>
                <div className="text-center mt-2 text-sm font-semibold text-purple-700">
                  Start Earning!
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Power Credit Purchase Modal */}
        <CreditPurchaseModal 
          isOpen={showCreditModal && false}
          onClose={() => setShowCreditModal(false)}
          onSuccess={() => {
            setShowCreditModal(false);
            loadData(); // Refresh credit balance
          }}
        />

        {/* Craft Credit Purchase Modal */}
        <CraftCreditPurchaseModal 
          isOpen={showCreditModal}
          onClose={() => setShowCreditModal(false)}
          onPurchaseComplete={() => {
            setShowCreditModal(false);
            loadData(); // Refresh craft credit balance
          }}
        />

        {/* Suggestion Box Modal */}
        {showSuggestionBox && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">💡</div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Suggestion Box</h3>
                    <p className="text-sm text-gray-600">Help us improve UPost! Share your ideas.</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Category Selection */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Category</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {[
                        { value: 'feature', label: '✨ New Feature', color: 'purple' },
                        { value: 'improvement', label: '🔧 Improvement', color: 'blue' },
                        { value: 'bug', label: '🐛 Bug Report', color: 'red' },
                        { value: 'other', label: '💬 Other', color: 'gray' }
                      ].map((cat) => (
                        <button
                          key={cat.value}
                          onClick={() => setSuggestionCategory(cat.value)}
                          className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                            suggestionCategory === cat.value
                              ? `border-${cat.color}-600 bg-${cat.color}-50 text-${cat.color}-900`
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {cat.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Suggestion Text */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Your Suggestion <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={suggestionText}
                      onChange={(e) => setSuggestionText(e.target.value)}
                      placeholder="Tell us your idea, improvement suggestion, or issue you're experiencing..."
                      className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent resize-none"
                      rows={6}
                      maxLength={1000}
                    />
                    <div className="text-xs text-gray-500 mt-1 text-right">
                      {suggestionText.length}/1000 characters
                    </div>
                  </div>

                  {/* Info Box */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-600 flex-shrink-0 mt-0.5">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="16" x2="12" y2="12"/>
                        <line x1="12" y1="8" x2="12.01" y2="8"/>
                      </svg>
                      <div className="text-sm text-blue-800">
                        <strong>We read every suggestion!</strong> Your feedback helps shape the future of UPost. 
                        We'll review your suggestion and may reach out if we need clarification.
                      </div>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowSuggestionBox(false);
                        setSuggestionText('');
                        setSuggestionCategory('feature');
                      }}
                      disabled={submittingSuggestion}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={async () => {
                        if (!suggestionText.trim()) {
                          alert('Please enter your suggestion');
                          return;
                        }

                        try {
                          setSubmittingSuggestion(true);
                          await api.request('/suggestions/submit', {
                            method: 'POST',
                            body: JSON.stringify({
                              category: suggestionCategory,
                              suggestion: suggestionText,
                              user_tier: user?.subscription_tier || 'free'
                            })
                          });

                          alert('🎉 Thank you! Your suggestion has been submitted successfully!');
                          setShowSuggestionBox(false);
                          setSuggestionText('');
                          setSuggestionCategory('feature');
                        } catch (err) {
                          alert('Failed to submit suggestion. Please try again.');
                        } finally {
                          setSubmittingSuggestion(false);
                        }
                      }}
                      disabled={submittingSuggestion || !suggestionText.trim()}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                    >
                      {submittingSuggestion ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mr-2">
                            <line x1="22" y1="2" x2="11" y2="13"/>
                            <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                          </svg>
                          Submit Suggestion
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
